/**
 * The two thin bars of the Office layout: the PATH bar above the screen (the
 * folders it sits in, its title as the last crumb, ⓘ, the pin star, full
 * screen and close) and the STATUS bar below the sheet tabs.
 */
import { Maximize2, Pencil, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { tw } from '../../../testHooks'
import { editorTitle } from '../../nav/parts/EditorsList'
import { Breadcrumb, InfoTip, PinButton } from '../../nav/parts/ScreenTitle'
import { S, iconButton } from '../../nav/tokens'
import type { RunNavItem } from '../../nav/types'
import type { ShellNavModel } from '../../nav/useShellNav'
import { IconTip, SR_ONLY } from './shared'

export function PathBar({ model, item, extras, onFolder, compact }: {
  model: ShellNavModel
  item: RunNavItem
  extras?: ReactNode
  onFolder: (folder: RunNavItem) => void
  compact: boolean
}) {
  return (
    <div {...tw('screen', `shell-title:${item.obj_name}`)} style={{
      position: 'relative', flexShrink: 0, height: compact ? 38 : 42, boxSizing: 'border-box',
      display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px 0 12px',
      background: S.panel, borderBottom: `1px solid ${S.border}`, minWidth: 0,
    }}>
      {/* The bar SHOWS the title as the breadcrumb's last crumb; the heading
          is for assistive technology's heading navigation. */}
      <h1 style={SR_ONLY}>{item.title}</h1>
      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center' }}>
        <Breadcrumb path={model.pathOf(item)} current={item} onFolder={onFolder} />
      </div>
      <InfoTip item={item} />
      <span style={{ flex: 1 }} />
      {extras}
      <PinButton pinned={model.isPinned(item)} onToggle={() => model.togglePin(item)} compact={compact} />
      <IconTip label="Full screen" align="right">
        <button type="button" className="tw-sh-btn" onClick={() => model.setFullScreen(true)}
          aria-label="Full screen" aria-pressed={false}
          {...tw('action', 'shell-fullscreen')}
          style={iconButton(34)}>
          <Maximize2 size={16} aria-hidden />
        </button>
      </IconTip>
      <IconTip label="Close this screen" align="right">
        <button type="button" className="tw-sh-btn" onClick={() => model.closeScreen(item)}
          aria-label={`Close ${item.title}`}
          {...tw('action', 'close_object')}
          style={iconButton(34)}>
          <X size={17} aria-hidden />
        </button>
      </IconTip>
    </div>
  )
}

export function StatusBar({ model, onOpenEditors }: {
  model: ShellNavModel
  onOpenEditors: (from: HTMLElement) => void
}) {
  const current = model.current
  const act = model.activeEditor
  const editing = !!act && act.objName === current?.obj_name
  const n = model.openScreens.length
  const info = editing && act
    ? `${act.mode === 'add' ? 'Adding' : 'Editing'} ${editorTitle(act)}`
    : current ? `${n} open screen${n === 1 ? '' : 's'}` : 'No screen open'
  const btn = {
    display: 'inline-flex', alignItems: 'center', gap: 6, height: 26, padding: '0 8px',
    border: 'none', borderRadius: 4, background: 'transparent', color: S.text2,
    fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap',
  } as const
  return (
    <div role="region" aria-label="Status bar" {...tw('action', 'shell-office-status')} style={{
      flexShrink: 0, height: 30, display: 'flex', alignItems: 'center', gap: 16, minWidth: 0,
      // Right padding: the Assistant's floating button sits over this corner.
      padding: '0 88px 0 12px', background: S.panel, borderTop: `1px solid ${S.border}`,
      fontSize: 13, color: S.text2,
    }}>
      <span aria-live="polite" className="tw-sh-clip" style={{ minWidth: 0 }}>Ready · {info}</span>
      <span style={{ flex: 1 }} />
      {model.editorsEnabled && (
        <button type="button" className="tw-oc-plain" onClick={(e) => onOpenEditors(e.currentTarget)}
          aria-haspopup="dialog"
          {...tw('action', 'shell-office-status-editors', { count: model.editors.length })}
          style={btn}>
          <Pencil size={14} aria-hidden /> Open editors: {model.editors.length}
        </button>
      )}
      <button type="button" className="tw-oc-plain" disabled={!current}
        onClick={() => model.setFullScreen(true)}
        {...tw('action', 'shell-office-status-fullscreen')}
        style={{ ...btn, opacity: current ? 1 : 0.5, cursor: current ? 'pointer' : 'default' }}>
        <Maximize2 size={14} aria-hidden /> Full screen
      </button>
    </div>
  )
}
